import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, UserCog, Info, Building2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { SimpleSelect } from '@/shared/ui/filters';
import { ContactModal } from '@/features/contact-modal/ui/contact-modal';
import { OrganizationFormModal } from '@/features/organization-form-modal';
import { cn } from '@/lib/utils';
import { useContactSelector } from '../model';

interface ContactSelectorProps {
  label: string;
  organizationId?: string;
  organizationName?: string;
  contactId?: string;
  contactName?: string;
  onOrganizationChange: (id: string | undefined) => void;
  onContactChange: (id: string | undefined) => void;
  className?: string;
}

export const ContactSelector = ({
  label,
  organizationId,
  organizationName,
  contactId,
  contactName,
  onOrganizationChange,
  onContactChange,
  className
}: ContactSelectorProps) => {
  const { t } = useTranslation();
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [orgModalMode, setOrgModalMode] = useState<'view' | 'edit' | 'create'>('view');

  const {
    isModalOpen,
    isEditing,
    showDetails,
    isLoadingOrgs,
    isLoadingContacts,
    orgOptions,
    contactOptions,
    selectedContact,
    handleAddClick,
    handleEditClick,
    handleModalClose,
    handleModalSuccess,
    toggleDetails
  } = useContactSelector({
    organizationId,
    organizationName,
    contactId,
    contactName,
    onOrganizationChange,
    onContactChange
  });

  const handleOrgDetailsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOrgModalMode('view');
    setIsOrgModalOpen(true);
  };

  const handleOrgEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOrgModalMode('edit');
    setIsOrgModalOpen(true);
  };

  const handleOrgModalClose = () => {
    setIsOrgModalOpen(false);
  };

  return (
    <div className={className}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 items-start">
        <Field>
          <FieldLabel>
            {label} {t('common.organization', 'Organization')}
          </FieldLabel>
          <div className="flex gap-2">
            <SimpleSelect
              options={orgOptions}
              value={organizationId}
              onChange={(val) => {
                onOrganizationChange(val);
                onContactChange(undefined);
              }}
              isLoading={isLoadingOrgs}
              placeholder={t('common.selectOrganization', 'Search organization')}
              className="flex-1 h-11"
            />
            {organizationId && (
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleOrgDetailsClick}
                  className="h-11 w-11 border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                  title={t('organizations.viewDetails', 'View Organization Details')}>
                  <Info className="w-4 h-4 text-slate-600" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleOrgEditClick}
                  className="h-11 w-11 border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                  title={t('organizations.editOrganization', 'Edit Organization')}>
                  <Building2 className="w-4 h-4 text-slate-600" />
                </Button>
              </div>
            )}
          </div>
        </Field>

        <Field>
          <FieldLabel>
            {label} {t('common.contact', 'Contact')}
          </FieldLabel>
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <SimpleSelect
                options={contactOptions}
                value={contactId}
                onChange={onContactChange}
                isLoading={isLoadingContacts}
                placeholder={t('common.selectContact', 'Select Contact')}
                className="w-full h-11"
              />
            </div>
            <div className="flex gap-1 shrink-0">
              {selectedContact && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={toggleDetails}
                    className={cn(
                      'h-11 w-11 border-slate-300 transition-colors',
                      showDetails
                        ? 'bg-green-100 border-green-400 text-green-700'
                        : 'bg-white hover:bg-slate-50 text-slate-600'
                    )}
                    title={t('contacts.showDetails', 'Show Details')}>
                    <Info className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleEditClick}
                    className="h-11 w-11 border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                    title={t('contacts.editContact', 'Edit Contact')}>
                    <UserCog className="w-4 h-4 text-slate-600" />
                  </Button>
                </>
              )}
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleAddClick}
                className="h-11 w-11 border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                title={t('contacts.addContact', 'Add Contact')}>
                <Plus className="w-4 h-4 text-slate-600" />
              </Button>
            </div>
          </div>
        </Field>
      </div>

      <ContactModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        initialData={isEditing ? selectedContact : { orgId: organizationId ? Number(organizationId) : undefined }}
      />

      <OrganizationFormModal
        isOpen={isOrgModalOpen}
        onClose={handleOrgModalClose}
        organizationId={Number(organizationId)}
        mode={orgModalMode}
        onSuccess={handleOrgModalClose}
      />

      {selectedContact && showDetails && (
        <div className="mt-4 p-4 rounded-lg bg-green-50 border border-green-100 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                {t('contacts.fields.fullNameJobTitle', "Person's full name / Job Title")}
              </div>
              <div className="text-sm font-medium text-slate-700">
                {selectedContact.name} {selectedContact.jobTitle ? `/ ${selectedContact.jobTitle}` : ''}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                  {t('contacts.fields.email', 'Email')}
                </div>
                <div className="text-sm font-medium text-slate-600 break-all">
                  {selectedContact.email || selectedContact.email1 || '-'}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                  {t('contacts.fields.phone', 'Phone')}
                </div>
                <div className="text-sm font-medium text-slate-600">
                  {selectedContact.phone || selectedContact.phone1 || '-'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
